import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UserControllerApi } from '../api/generated';
import type {
  CreateUserRequest,
  UpdateUserRequest,
  UserDetailResponse
} from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { getErrorMessage } from '../utils/errorMessageUtils';

export interface UseUsersResult {
  users: UserDetailResponse[];
  isLoading: boolean;
  loadError: string | null;
  refresh: () => Promise<void>;
  createUser: (payload: CreateUserRequest) => Promise<void>;
  updateUser: (userId: string, payload: UpdateUserRequest) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
}

export function useUsers(): UseUsersResult {
  const [users, setUsers] = useState<UserDetailResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);
  const api = useMemo(() => new UserControllerApi(createApiConfiguration()), []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await api.getAllUsers();
      setUsers(response.data);
    } catch (e) {
      setLoadError(getErrorMessage(e, 'Failed to load users'));
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    void refresh();
  }, [refresh]);

  const createUser = useCallback(
    async (payload: CreateUserRequest) => {
      const response = await api.createUser({ createUserRequest: payload });
      const created: UserDetailResponse = {
        userId: response.data.userId,
        email: payload.email,
        firstName: payload.firstName,
        lastName: payload.lastName,
        accountAdmin: payload.accountAdmin ?? false,
        locationPermissions: payload.locationPermissions.map((p) => ({
          locationId: p.locationId,
          read: p.read ?? false,
          write: p.write ?? false,
          patientBooker: p.patientBooker ?? false
        }))
      };
      setUsers((prev) => [...prev, created]);
    },
    [api]
  );

  const updateUser = useCallback(
    async (userId: string, payload: UpdateUserRequest) => {
      const response = await api.updateUser({ userId, updateUserRequest: payload });
      setUsers((prev) => prev.map((u) => (u.userId === userId ? response.data : u)));
    },
    [api]
  );

  const deleteUser = useCallback(
    async (userId: string) => {
      await api.deleteUser({ userId });
      setUsers((prev) => prev.filter((u) => u.userId !== userId));
    },
    [api]
  );

  return { users, isLoading, loadError, refresh, createUser, updateUser, deleteUser };
}
